import "firebase/compat/auth";
import { useEffect } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import IdleOverlay from "./components/globals/IdleOverlay";
import { logoutUser, onMessageListener, requestForToken } from "./firebase/utility";
import Admin from "./layout/AdminLayout";
import Auth from "./layout/AuthLayout";
import { setUser } from "./store/actions/actions";
import useIdleTimeout from "./views/Admin/customHooks/useIdleTimeout";

function App() {
  const { user } = useSelector((state) => state.auth);
  const { themeColor } = useSelector((state) => state.redux);
  const dispatch = useDispatch();

  // Timeouts in milliseconds:
  // Production values: LOGOUT_TIMEOUT = 2400000 (60 min), WARNING_TIMEOUT = 1200000 (20 min)
  // For testing: set LOGOUT_TIMEOUT = 40000 (40s), WARNING_TIMEOUT = 20000 (20s)
  const LOGOUT_TIMEOUT = 3600000;
  const WARNING_TIMEOUT = 1200000;

  const { showIdleModal, remainingTime, handleResume, handleLogout } = useIdleTimeout(LOGOUT_TIMEOUT, WARNING_TIMEOUT);

  // On app load: check if user was idle beyond timeout while system was off (closed)
  useEffect(() => {
    const lastActivity = localStorage.getItem('lastActivity');
    const currentTime = Date.now();
    if (lastActivity && currentTime - parseInt(lastActivity, 10) > LOGOUT_TIMEOUT) {
      localStorage.removeItem('lastActivity');
      logoutUser().then(() => {
        dispatch(setUser({}));
      });
    }
  }, [dispatch]);

  useEffect(() => {

    let unsubscribe = null;
    if (user?._id && user?.role === 'Super-admin') {
      requestForToken(user._id);

      unsubscribe = onMessageListener((payload) => {
        // Show in-app toast notification
        if (payload?.notification) {
          toast.success(`${payload.notification.title}: ${payload.notification.body}`, {
            duration: 5000,
            position: 'top-right',
          });

          if (Notification.permission === 'granted') {
            new Notification(payload.notification.title, {
              body: payload.notification.body,
              icon: '/favicon.ico'
            });
          }
        }
      });
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user]);

  useEffect(() => {
    if (themeColor) {
      document.documentElement.style.setProperty('--bg-primary-color', themeColor);
      document.documentElement.style.setProperty('--primary-blue', themeColor);
      document.documentElement.style.setProperty('--light-primary-color', themeColor + '1a');
    }
  }, [themeColor]);

  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            borderRadius: "10px",
            fontSize: "14px",
            fontWeight: "500",
            padding: "12px 18px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            color: "#1e293b",
          },
          success: {
            duration: 4000,
            style: {
              border: "1px solid #bbf7d0",
            },
          },
          error: {
            duration: 5000,
            style: {
              border: "1px solid #fecaca",
            },
          },
        }}
      />

      {/* Idle session overlay — only show when user is logged in */}
      {user?._id && showIdleModal && (
        <IdleOverlay onResume={handleResume} onLogout={handleLogout} remainingTime={remainingTime} />
      )}

      <Switch>
        {user?._id ? (
          <Switch>
            <Route path="/admin" render={(props) => <Admin {...props} />} />
            <Redirect from="/" to="/admin/index" />
          </Switch>
        ) : (
          <Switch>
            <Route path="/auth" render={(props) => <Auth {...props} />} />
            <Redirect from="/" to="/auth/login" />
          </Switch>
        )}
      </Switch>
    </BrowserRouter>
  );
}
export default App;

